import { SimWorld, addLog, isGroupModeEnabled, isAsymmetricEvolutionEnabled, getGroupConfigForNpc, isGroupFixedPopulation } from '../types/SimWorld';
import { SimNPC, createSimNPC, CardStat, isCardForbidden } from '../types/SimNPC';
import { getGroupRespawnConfig, isFixedPopulationGroup } from '../types/Group';
import { POPULATION_CONSTANTS } from '../core/PopulationConstants';
import type { CardWithId } from '../../types/card';
import { getAvailableCards } from '../utils/CardFilter';
import type { ExtendedCardData } from '../../utils/cardManager';

const { CULL_MIN_BATTLES, CULL_WIN_RATE_THRESHOLD, CULL_CHECK_INTERVAL } =
  POPULATION_CONSTANTS;

/** リスポーン時に必要な情報 */
interface RespawnContext {
  world: SimWorld;
  allCards: ExtendedCardData[];
  deadNpc: SimNPC;
}

/**
 * 死亡処理（寿命システム）
 * @param allCards 全カードデータ（固定人口モードのリスポーン用）
 */
export function processDeaths(world: SimWorld, allCards?: ExtendedCardData[]): void {
  const { npcs } = world;
  const respawnQueue: SimNPC[] = [];

  for (const npc of npcs) {
    if (!npc.isAlive) continue;

    // 寿命チェック: daysLived >= maxLifespan
    if (npc.daysLived >= npc.maxLifespan) {
      killNPC(npc, world, '寿命');

      // 固定人口モードならリスポーンキューに追加
      if (npc.group && isGroupFixedPopulation(npc.group, world)) {
        respawnQueue.push(npc);
      }
    }
  }

  // リスポーン処理
  if (allCards && respawnQueue.length > 0) {
    for (const deadNpc of respawnQueue) {
      respawnNPC({ world, allCards, deadNpc });
    }
  }
}

/**
 * デュエル淘汰処理
 * 10戦以上で勝率10%未満のNPCは淘汰される
 * 非対称進化モードではrecorded勝率で判定
 * @param allCards 全カードデータ（固定人口モードのリスポーン用）
 */
export function processCulling(world: SimWorld, allCards?: ExtendedCardData[]): void {
  const { npcs, day } = world;
  const useRecordedStats = isAsymmetricEvolutionEnabled(world);
  const respawnQueue: SimNPC[] = [];

  // 7日ごとにチェック
  if (day % CULL_CHECK_INTERVAL !== 0) return;

  for (const npc of npcs) {
    if (!npc.isAlive) continue;

    // 非対称進化モードではrecorded統計を使用
    const wins = useRecordedStats ? npc.recordedWins : npc.wins;
    const losses = useRecordedStats ? npc.recordedLosses : npc.losses;
    const totalBattles = wins + losses;

    // 10戦以上で勝率10%未満なら淘汰
    if (totalBattles >= CULL_MIN_BATTLES) {
      const winRate = wins / totalBattles;

      if (winRate < CULL_WIN_RATE_THRESHOLD) {
        npc.isAlive = false;
        npc.health = 0;

        // グループ統計の死亡数を更新
        updateGroupDeathStats(npc, world);

        const winRatePercent = (winRate * 100).toFixed(1);
        const statsLabel = useRecordedStats ? '記録対象' : '';
        addLog(
          world,
          `⚰️ ${npc.name}は連敗により衰弱死した ` +
            `(${statsLabel}${wins}勝${losses}敗, 勝率${winRatePercent}%)`
        );

        // 固定人口モードならリスポーンキューに追加
        if (npc.group && isGroupFixedPopulation(npc.group, world)) {
          respawnQueue.push(npc);
        }
      }
    }
  }

  // リスポーン処理
  if (allCards && respawnQueue.length > 0) {
    for (const deadNpc of respawnQueue) {
      respawnNPC({ world, allCards, deadNpc });
    }
  }
}

/**
 * NPCを死亡させる
 */
function killNPC(npc: SimNPC, world: SimWorld, cause: string): void {
  npc.isAlive = false;

  // グループ統計の死亡数を更新
  updateGroupDeathStats(npc, world);

  addLog(
    world,
    `💀 ${npc.name}が${cause}により死亡した（享年${npc.daysLived}日）`
  );
}

/**
 * グループ統計の死亡数を更新
 */
function updateGroupDeathStats(npc: SimNPC, world: SimWorld): void {
  if (!isGroupModeEnabled(world) || !npc.group || !world.groupStats) return;

  const stats = world.groupStats.get(npc.group);
  if (stats) {
    stats.deathCount++;
    // aliveCountはtakeDailySnapshotで再計算されるが、即時反映のためここでも減算
  }
}

// === 固定人口モード: リスポーン処理 ===

/**
 * 死亡NPCをリスポーン（固定人口モード用）
 */
function respawnNPC(context: RespawnContext): void {
  const { world, allCards, deadNpc } = context;
  const groupConfig = getGroupConfigForNpc(deadNpc, world);
  if (!groupConfig) return;

  const respawnConfig = getGroupRespawnConfig(groupConfig);
  const DECK_SIZE = 40;

  // 使用可能カードプールを取得
  const availableCards = getAvailableCards(allCards, groupConfig.cardPoolConfig);
  if (availableCards.length === 0) {
    addLog(world, `⚠️ ${deadNpc.name}のリスポーン失敗: カードプールが空`);
    return;
  }

  // 各継承元の枚数を計算
  const selfCount = Math.floor(DECK_SIZE * respawnConfig.selfCardRatio);
  const groupCount = Math.floor(DECK_SIZE * respawnConfig.groupCardRatio);
  const randomCount = DECK_SIZE - selfCount - groupCount;

  // デッキを構築
  const newDeck: CardWithId[] = [];
  let forbiddenCount = 0;
  const cardCounts = new Map<string, number>(); // 全体での同名カード枚数管理

  // 1. 死亡NPCの上位カード（非対称モードではrecorded統計を使用）
  const useRecordedStats = isAsymmetricEvolutionEnabled(world);
  const selfCards = getTopWinRateCards(deadNpc, selfCount, availableCards, useRecordedStats, forbiddenCount);

  // 3枚制限を考慮して追加
  for (const card of selfCards) {
    const currentCount = cardCounts.get(card.id) || 0;
    if (currentCount < 3) {
      newDeck.push(card);
      cardCounts.set(card.id, currentCount + 1);
      if (isCardForbidden(card)) forbiddenCount++;
    }
  }

  // 2. グループ上位NPCのカード
  const groupCards = getTopGroupCards(world, deadNpc.group!, groupCount, availableCards, useRecordedStats, forbiddenCount);

  // 3枚制限を考慮して追加
  for (const card of groupCards) {
    const currentCount = cardCounts.get(card.id) || 0;
    if (currentCount < 3) {
      newDeck.push(card);
      cardCounts.set(card.id, currentCount + 1);
      if (isCardForbidden(card)) forbiddenCount++;
    }
  }

  // 3. ランダムカード（禁忌カード制限を考慮）
  const randomCards = getRandomCards(availableCards, randomCount, newDeck, forbiddenCount, cardCounts);
  newDeck.push(...randomCards);
  forbiddenCount += randomCards.filter(c => isCardForbidden(c)).length;

  // デッキサイズを調整（足りない場合はランダムで補完、禁忌カード＆3枚制限を考慮）
  while (newDeck.length < DECK_SIZE) {
    // 3枚制限 + 禁忌カード制限を考慮したカード選択
    let candidates = availableCards.filter(c => {
      const currentCount = cardCounts.get(c.id) || 0;
      return currentCount < 3;
    });

    // 禁忌カードが既に1枚ある場合、禁忌カードを除外
    if (forbiddenCount >= 1) {
      const validCandidates = candidates.filter(c => !isCardForbidden(c));
      if (validCandidates.length > 0) {
        candidates = validCandidates;
      }
    }

    if (candidates.length === 0) break; // これ以上追加できない

    const card = candidates[Math.floor(Math.random() * candidates.length)];
    cardCounts.set(card.id, (cardCounts.get(card.id) || 0) + 1);

    if (isCardForbidden(card)) {
      forbiddenCount++;
    }

    newDeck.push({ ...card, uniqueId: `${card.id}_${Date.now()}_${Math.random().toString(36).slice(2, 5)}` });
  }

  // 新NPCを作成
  const generation = deadNpc.generation + 1;
  const newNpc = createSimNPC(
    generateRespawnName(deadNpc.name, generation),
    newDeck,
    {
      position: { ...deadNpc.position }, // 同じ位置にリスポーン
      generation,
      birthDay: world.day,
      group: deadNpc.group,
      maxLifespan: 60, // リスポーン時は60日固定
    }
  );

  // ワールドに追加
  world.npcs.push(newNpc);

  addLog(
    world,
    `🔄 ${newNpc.name}がリスポーン（第${generation}世代, デッキ: 本人${selfCards.length}枚/グループ${groupCards.length}枚/ランダム${randomCards.length}枚）`
  );
}

/**
 * NPCの上位勝率カードを取得
 * 同名カードは最大3枚まで継承可能、禁忌カードは1枚まで
 */
function getTopWinRateCards(
  npc: SimNPC,
  count: number,
  availableCards: ExtendedCardData[],
  useRecordedStats: boolean,
  currentForbiddenCount: number
): CardWithId[] {
  const cardStats = useRecordedStats ? npc.recordedCardStats : npc.cardStats;
  const availableIds = new Set(availableCards.map(c => c.id));

  // デッキ内の全カードを勝率付きで保持（重複を除外しない）
  const cardWinRates: { cardId: string; winRate: number; card: CardWithId }[] = [];

  for (const card of npc.deck) {
    if (!availableIds.has(card.id)) continue;

    const stat = cardStats.get(card.id);
    const winRate = stat && stat.gamesWithCard > 0
      ? stat.winsWithCard / stat.gamesWithCard
      : 0.5; // 統計がなければ50%とする

    cardWinRates.push({ cardId: card.id, winRate, card });
  }

  // 勝率順にソート（同率の場合はランダム）
  cardWinRates.sort((a, b) => {
    if (b.winRate !== a.winRate) return b.winRate - a.winRate;
    return Math.random() - 0.5;
  });

  // 上位カードを取得（同名カード3枚制限、禁忌カード1枚制限を適用）
  const result: CardWithId[] = [];
  const cardCounts = new Map<string, number>();
  let forbiddenCount = currentForbiddenCount;

  for (const item of cardWinRates) {
    if (result.length >= count) break;

    const currentCount = cardCounts.get(item.cardId) || 0;
    if (currentCount >= 3) continue; // 3枚制限

    // 禁忌カード制限
    if (isCardForbidden(item.card) && forbiddenCount >= 1) continue;

    cardCounts.set(item.cardId, currentCount + 1);
    if (isCardForbidden(item.card)) forbiddenCount++;

    result.push({
      ...item.card,
      uniqueId: `${item.card.id}_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
    });
  }

  return result;
}

/**
 * グループ上位NPCのカードを取得
 * 同名カードは最大3枚まで継承可能、禁忌カードは1枚まで
 */
function getTopGroupCards(
  world: SimWorld,
  groupId: string,
  count: number,
  availableCards: ExtendedCardData[],
  useRecordedStats: boolean,
  currentForbiddenCount: number
): CardWithId[] {
  // 同グループの生存NPCをレーティング順にソート
  const groupMembers = world.npcs.filter(
    n => n.isAlive && n.group === groupId
  ).sort((a, b) => b.rating - a.rating);

  if (groupMembers.length === 0) return [];

  // 上位3人のデッキからカードを収集
  const topNpcs = groupMembers.slice(0, 3);
  const cardWinRates: { cardId: string; winRate: number; card: CardWithId }[] = [];
  const availableIds = new Set(availableCards.map(c => c.id));

  for (const npc of topNpcs) {
    const cardStats = useRecordedStats ? npc.recordedCardStats : npc.cardStats;

    for (const card of npc.deck) {
      if (!availableIds.has(card.id)) continue;

      const stat = cardStats.get(card.id);
      const winRate = stat && stat.gamesWithCard > 0
        ? stat.winsWithCard / stat.gamesWithCard
        : 0.5;

      cardWinRates.push({ cardId: card.id, winRate, card });
    }
  }

  // 勝率順にソート（同率の場合はランダム）
  cardWinRates.sort((a, b) => {
    if (b.winRate !== a.winRate) return b.winRate - a.winRate;
    return Math.random() - 0.5;
  });

  // 上位カードを取得（同名カード3枚制限、禁忌カード1枚制限を適用）
  const result: CardWithId[] = [];
  const cardCounts = new Map<string, number>();
  let forbiddenCount = currentForbiddenCount;

  for (const item of cardWinRates) {
    if (result.length >= count) break;

    const currentCount = cardCounts.get(item.cardId) || 0;
    if (currentCount >= 3) continue; // 3枚制限

    // 禁忌カード制限
    if (isCardForbidden(item.card) && forbiddenCount >= 1) continue;

    cardCounts.set(item.cardId, currentCount + 1);
    if (isCardForbidden(item.card)) forbiddenCount++;

    result.push({
      ...item.card,
      uniqueId: `${item.card.id}_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
    });
  }

  return result;
}

/**
 * ランダムカードを取得（重複を考慮、禁忌カード制限を考慮）
 * @param cardCounts 既存デッキのカードID別枚数（外部で管理、更新される）
 */
function getRandomCards(
  availableCards: ExtendedCardData[],
  count: number,
  existingDeck: CardWithId[],
  currentForbiddenCount: number,
  cardCounts: Map<string, number>
): CardWithId[] {
  const result: CardWithId[] = [];
  let forbiddenCount = currentForbiddenCount;

  for (let i = 0; i < count; i++) {
    // ランダムにカードを選択（最大3枚まで、禁忌カード制限を考慮）
    let candidates = availableCards.filter(c => {
      const currentCount = cardCounts.get(c.id) || 0;
      return currentCount < 3;
    });

    // 禁忌カードが既に1枚ある場合、禁忌カードを除外
    if (forbiddenCount >= 1) {
      const validCandidates = candidates.filter(c => !isCardForbidden(c));
      if (validCandidates.length > 0) {
        candidates = validCandidates;
      }
    }

    if (candidates.length === 0) break;

    const card = candidates[Math.floor(Math.random() * candidates.length)];
    cardCounts.set(card.id, (cardCounts.get(card.id) || 0) + 1);

    if (isCardForbidden(card)) {
      forbiddenCount++;
    }

    result.push({
      ...card,
      uniqueId: `${card.id}_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
    } as CardWithId);
  }

  return result;
}

/**
 * リスポーン用の名前を生成
 */
function generateRespawnName(originalName: string, generation: number): string {
  // 既存の全ての世代表記を削除（「○世」形式を全て削除）
  // 例: "キャト II世 III世" → "キャト"
  const baseName = originalName.replace(/\s+(II|III|IV|V|VI|VII|VIII|IX|X|\d+)世/g, '').trim();

  // ローマ数字で世代を表現（簡易版）
  const romanNumerals = ['', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
  const romanGen = generation <= 10 ? romanNumerals[generation - 1] : `${generation}`;

  return romanGen ? `${baseName} ${romanGen}世` : baseName;
}
